/**
 * Add CRM contacts to a static list: search, tick, add.
 *
 * The selection is held by id ACROSS searches, so somebody can search for
 * three schools in turn and add all of them in one request. A contact with no
 * email address is listed (it is still a contact) but marked, because it will
 * be counted as excluded at send time rather than mailed.
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Checkbox, Input, Modal, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import { pluralise } from '@/lib/format';
import type { Contact } from '@/lib/types';
import { useDebounced } from './ref-options';

interface Picked {
  id: number;
  name: string;
}

export function AddContactsModal({
  open,
  listId,
  onClose,
  onAdded,
}: {
  open: boolean;
  listId: number;
  onClose(): void;
  onAdded(summary: { added: number; alreadyPresent: number }): void;
}) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search.trim());
  const [rows, setRows] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Map<number, Picked>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listContacts({ page: 1, perPage: 25, ...(debounced ? { search: debounced } : {}) }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRows(response.data);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'Contacts could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, debounced]);

  const close = () => {
    if (saving) return;
    setSearch('');
    setPicked(new Map());
    setSaveError(null);
    onClose();
  };

  const toggle = (contact: Contact) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(contact.id)) next.delete(contact.id);
      else next.set(contact.id, { id: contact.id, name: contact.name });
      return next;
    });
  };

  const submit = async () => {
    if (saving || picked.size === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const response = await api.addMarketingListMembers(listId, { contactIds: [...picked.keys()] });
      setSearch('');
      setPicked(new Map());
      onAdded(response.data);
    } catch (err) {
      setSaveError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'The contacts could not be added.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add contacts"
      size="lg"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-fg-muted tabular">{pluralise(picked.size, 'contact')} selected</span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="secondary" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} loading={saving} disabled={picked.size === 0}>
              Add to list
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-3">
        {saveError && (
          <Notice tone="danger" role="alert">
            {saveError}
          </Notice>
        )}
        <Input
          type="search"
          aria-label="Search contacts"
          placeholder="Search contacts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        {picked.size > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {[...picked.values()].map((p) => (
              <Badge key={p.id} tone="neutral" variant="outline">
                {p.name}
              </Badge>
            ))}
          </div>
        )}
        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : loading && rows.length === 0 ? (
          <div className="grid gap-2">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="m-0 py-6 text-center text-sm text-fg-muted">No contacts match.</p>
        ) : (
          <ul className="m-0 grid max-h-[50vh] list-none divide-y divide-border overflow-y-auto rounded-md border border-border p-0">
            {rows.map((contact) => (
              <li key={contact.id} className="flex items-start justify-between gap-3 px-3 py-2">
                <Checkbox
                  checked={picked.has(contact.id)}
                  onChange={() => toggle(contact)}
                  label={
                    <span className="grid min-w-0">
                      <span className="truncate text-fg">{contact.name}</span>
                      <span className="truncate text-xs text-fg-muted">
                        {[contact.email, contact.company?.name].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </span>
                  }
                />
                {!contact.email && <Badge tone="warning">No email</Badge>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
